import withStore, { TLoadingAwarePresenter, TPresentable } from "../withStore";
import { IReportingPeriodItem } from "../../model/report/ReportingPeriodItem";
import Application from "../../Application";
import GetReport3DataInteractor from "../../interactor/report/GetReport3DataInteractor";
import { DateService } from "../../service/DateService";
import { IReport3Applicant } from "../../model/report/Report3Applicant";

export interface TReport3Presenter extends TLoadingAwarePresenter {
  reportingPeriodList: IReportingPeriodItem[];
  report3CurrentReportingPeriod?: IReportingPeriodItem;
  report3?: IReport3Applicant[];
  report3PeriodFrom?: Date;
  report3PeriodTo?: Date;
  report3Min?: number;
  report3Max?: number;
  report3SubmitReason?: string;
}

export interface IReport3Presenter extends TReport3Presenter, TPresentable {
  loadReportingPeriodList(reportingPeriodList: IReportingPeriodItem[]): void;
  onChangeReportingPeriod(reportingPeriodId: number): void;
  getPDFReport3(
    reportingPeriodId: number,
    periodFrom: Date,
    periodTo: Date,
    min: number,
    max: number,
    submitReason: string
  ): Promise<any>;
  setPeriodFrom(periodFrom: Date): void;
  setPeriodTo(periodTo: Date): void;
  setMin(min: number): void;
  setMax(max: number): void;
  setSubmitReason(submitReason: string): void;
  getXLSXReport3Link(): string;
}

const defaultState: TReport3Presenter = {
  reportingPeriodList: []
};

const Report3Presenter = withStore<IReport3Presenter, TReport3Presenter>(
  ({ application, store, loader, translate }): IReport3Presenter => {
    const _store = store;
    const _application: Application = application;
    const state = _store.getState<TReport3Presenter>();

    const loadReportingPeriodList = (
      reportingPeriodList: IReportingPeriodItem[]
    ) => {
      _store.update({
        reportingPeriodList
      });
    };

    const setPeriodFrom = (report3PeriodFrom?: Date) => {
      _store.update({ report3PeriodFrom, report3: undefined });
    };

    const setPeriodTo = (report3PeriodTo?: Date) => {
      _store.update({ report3PeriodTo, report3: undefined });
    };

    const setMin = (report3Min?: number) => {
      _store.update({ report3Min, report3: undefined });
    };

    const setMax = (report3Max?: number) => {
      _store.update({ report3Max, report3: undefined });
    };

    const setSubmitReason = (report3SubmitReason?: string) => {
      _store.update({ report3SubmitReason, report3: undefined });
    };

    const onChangeReportingPeriod = (reportingPeriodId: number) => {
      const reportingPeriodList = _store.getState<TReport3Presenter>()
        .reportingPeriodList;
      const report3CurrentReportingPeriod = reportingPeriodList.find(
        (reportingPeriod) => reportingPeriod.id === reportingPeriodId
      );
      _store.update({
        report3CurrentReportingPeriod,
        report3: undefined
      });
    };

    const getPDFReport3 = async () => {
      const {
        report3CurrentReportingPeriod,
        report3PeriodFrom,
        report3PeriodTo,
        report3Min,
        report3Max,
        report3SubmitReason
      } = _store.getState<TReport3Presenter>();
      loader.start("report3Loader");
      const report3 =
        report3CurrentReportingPeriod &&
        report3PeriodFrom &&
        report3PeriodTo &&
        report3Min != null &&
        report3Max != null &&
        (await application.container
          .resolve<GetReport3DataInteractor>("getReport3Data")
          .execute(
            report3CurrentReportingPeriod.id,
            report3PeriodFrom,
            report3PeriodTo,
            report3Min,
            report3Max,
            report3SubmitReason
          ));
      _store.update({ report3 });
      loader.stop("report3Loader");
    };

    const parseDateToTimestamp = (date: Date) => {
      return DateService().parseDateToTimestamp(date);
    };

    const getXLSXReport3Link = (): string => {
      const {
        report3CurrentReportingPeriod,
        report3PeriodFrom,
        report3PeriodTo,
        report3Min,
        report3Max,
        report3SubmitReason
      } = _store.getState<TReport3Presenter>();

      let periodFromTimestamp =
        report3PeriodFrom && parseDateToTimestamp(report3PeriodFrom);
      let periodToTimestamp =
        report3PeriodTo && parseDateToTimestamp(report3PeriodTo);

      let path = `/api/reports/report3/reporting-periods/${report3CurrentReportingPeriod?.id}/xlsx?periodFrom=${periodFromTimestamp}&periodTo=${periodToTimestamp}&min=${report3Min}&max=${report3Max}`;

      if (report3SubmitReason) {
        path = `${path}&submitReason=${report3SubmitReason}`;
      }

      return path;
    };

    return {
      ...state,
      store: _store,
      loader,
      application: _application,
      translate,
      loadReportingPeriodList,
      onChangeReportingPeriod,
      getPDFReport3,
      setPeriodFrom,
      setPeriodTo,
      setMin,
      setMax,
      setSubmitReason,
      getXLSXReport3Link
    };
  },
  defaultState
);

export default Report3Presenter;
